import { OverlayContainer } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MomentDateModule } from '@angular/material-moment-adapter';
import { RouterModule } from '@angular/router';
import {DropdownModule} from 'primeng/dropdown';
import {AutoCompleteModule} from 'primeng/autocomplete';
import { DateResponseInterceptor } from '../core/interceptors/data-response.interceptor';
import { AccessPointService } from '../core/services/access-point.service';
import { NotificationService } from '../core/services/notifications.service';
import { PanelOutputsService } from '../core/services/panel-outputs.service';
import { UserService } from '../core/services/user.service';
import { AccessPointProgressDialogComponent } from '../modules/access-point/components/access-point-details/components/access-point-progress-dialog/access-point-progress-dialog.component';
import { AlertListComponent } from '../modules/access-point/components/alert-list/alert-list.component';
import { ProgressStatusPipe } from '../modules/access-point/pipes/progress-status.pipe';
import { AngularMaterialModule } from '../scss/angular-material/angular-material.module';
import { AppDatePickerComponent } from './components/app-date-picker/app-date-picker.component';
import { CalendarHeaderComponent } from './components/app-date-picker/calendar-header/calendar-header.component';
import { ButtonSwitcherComponent } from './components/button-switcher/button-switcher.component';
import { CheckboxComponent } from './components/checkbox/checkbox.component';
import { FilterBarComponent } from './components/filter-bar/filter-bar.component';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { IconButtonComponent } from './components/icon-button/icon-button.component';
import { ModulesBarComponent } from './components/modules-bar/modules-bar.component';
import { RadioButtonsComponent } from './components/radio-buttons/radio-buttons.component';
import { SearchComponent } from './components/search/search.component';
import { SecuredButtonComponent } from './components/secured-button/secured-button.component';
import { SelectComponent } from './components/select/select.component';
import { SvgComponent } from './components/svg/svg.component';
import { TimeInputComponent } from './components/time-input/time-input.component';
import { ClickStopPropagationDirective } from './directives/click-stop-propagation.directive';
import { LockActionsComponent } from './lock-actions/lock-actions.component';
import { LockdownComponent } from './lockdown/lockdown.component';
import { OutputLockingStatusComponent } from './output-locking-status/output-locking-status.component';
import { OutputOpeningStatusComponent } from './output-opening-status/output-opening-status.component';
import { PageNotFoundComponent} from './pages/page-not-found/page-not-found.component';
import { ResizableDirective } from './directives/resizable.directive';
import { MinMaxDirective } from './directives/min-max.directive';
import { DirectionTextPipe } from './pipes/direction-text.pipe';
import { TicksToDate } from './pipes/ticks-to-date';
import { CustomDatePipe } from './pipes/custom-date.pipe';
import { DirectionIconPipe } from './pipes/direction-icon.pipe';
import { SecondsToMinutesPipe } from './pipes/seconds-to-minutes.pipe';
import { ResolveUserPhotoPipe } from './pipes/resolve-user-photo.pipe';
import { CamelcaseToSeparatedWords } from './pipes/camelcase-to-separated-words';
import { EllipsisTextDirective } from './directives/ellipsis-text.directive';
import { PrintJsonPipe } from './pipes/print-json.pipe';
import { TimezoneDescription } from '../modules/users/components/user-details/components/permission-details/permission-details.component';
import { TimeRangeComponent } from './components/time-range/time-range.component';
import { TrimSpacesPipe } from './pipes/trim-spaces.pipe';
import { NetworkFiltering } from './pipes/networks-filtering.pipe';
import {MakeFirstLatterUppercasePipe} from './pipes/first-latter-uppercase.pipe';
import {MakeFirstLatterUppercaseForArrayPipe} from './pipes/make-first-latter-uppercase-for-array.pipe';
import {TodayDate} from './pipes/today-date';
import {CleanBackslash} from './pipes/clean-backslash';
import { AddAdditionalZeroToTimePipe } from './pipes/add-additional-zero-to-time.pipe';
import {EnterToInPipe} from './pipes/enterToIn.pipe';
import {AuthorizationUpdatedComponent} from './components/snack-bar/authorization-updated/authorization-updated.component';
import {ServerDisconnectedErrorComponent} from './components/snack-bar/server-disconnected-error/server-disconnected-error.component';
import {NetworkConnectionErrorComponent} from './components/snack-bar/network-connection-error/network-connection-error.component';
import {MonitoringService} from '../services/logging.service';
import {TranslateModule} from '@ngx-translate/core';
import {ChangeSpaceToDashPipe} from './pipes/change-space-to-dash.pipe';
import {DarkModeToggleComponent} from './components/dark-mode-toggle/dark-mode-toggle.component';
import {HoursMinutesInputComponent} from './components/hours-minutes-input/hours-minutes-input.component';
import {AlertMinimizedListComponent} from './components/alert-minimized-list/alert-minimized-list.component';
import { OpenButtonComponent } from './components/open-button/open-button.component';

@NgModule({
    declarations: [
        AlertMinimizedListComponent,
        PageNotFoundComponent,
        HeaderComponent,
        SearchComponent,
        OutputOpeningStatusComponent,
        OutputLockingStatusComponent,
        LockActionsComponent,
        OutputLockingStatusComponent,
        LockdownComponent,
        ModulesBarComponent,
        CustomDatePipe,
        TicksToDate,
        ClickStopPropagationDirective,
        SelectComponent,
        TimeInputComponent,
        HoursMinutesInputComponent,
        TimeRangeComponent,
        SvgComponent,
        CheckboxComponent,
        AppDatePickerComponent,
        CalendarHeaderComponent,
        ButtonSwitcherComponent,
        RadioButtonsComponent,
        IconButtonComponent,
        SecondsToMinutesPipe,
        ResolveUserPhotoPipe,
        FilterBarComponent,
        DirectionIconPipe,
        SecuredButtonComponent,
        AlertListComponent,
        AccessPointProgressDialogComponent,
        ProgressStatusPipe,
        FooterComponent,
        ResizableDirective,
        EllipsisTextDirective,
        MinMaxDirective,
        DirectionTextPipe,
        PrintJsonPipe,
        TrimSpacesPipe,
        TimezoneDescription,
        CamelcaseToSeparatedWords,
        NetworkFiltering,
        MakeFirstLatterUppercasePipe,
        MakeFirstLatterUppercaseForArrayPipe,
        CleanBackslash,
        TodayDate,
        AddAdditionalZeroToTimePipe,
        ChangeSpaceToDashPipe,
        EnterToInPipe,
        AuthorizationUpdatedComponent,
        ServerDisconnectedErrorComponent,
        DarkModeToggleComponent,
        NetworkConnectionErrorComponent,
        OpenButtonComponent
    ],
    imports: [
        CommonModule,
        FormsModule,
        AngularMaterialModule,
        HttpClientModule,
        RouterModule,
        DropdownModule,
        MatDatepickerModule,
        MatSnackBarModule,
        MatNativeDateModule,
        MomentDateModule,
        FormsModule,
        ReactiveFormsModule,
        AutoCompleteModule,
        TranslateModule
    ],
    exports: [
        AlertMinimizedListComponent,
        PageNotFoundComponent,
        HeaderComponent,
        SearchComponent,
        OutputOpeningStatusComponent,
        LockActionsComponent,
        OutputLockingStatusComponent,
        ModulesBarComponent,
        ClickStopPropagationDirective,
        CustomDatePipe,
        TrimSpacesPipe,
        TicksToDate,
        SelectComponent,
        TimeInputComponent,
        HoursMinutesInputComponent,
        TimeRangeComponent,
        FormsModule,
        SvgComponent,
        CheckboxComponent,
        AppDatePickerComponent,
        ReactiveFormsModule,
        ButtonSwitcherComponent,
        RadioButtonsComponent,
        IconButtonComponent,
        SecondsToMinutesPipe,
        ResolveUserPhotoPipe,
        FilterBarComponent,
        SecuredButtonComponent,
        DirectionIconPipe,
        AlertListComponent,
        AccessPointProgressDialogComponent,
        ProgressStatusPipe,
        FooterComponent,
        ResizableDirective,
        EllipsisTextDirective,
        MinMaxDirective,
        DirectionTextPipe,
        PrintJsonPipe,
        TimezoneDescription,
        CamelcaseToSeparatedWords,
        NetworkFiltering,
        MakeFirstLatterUppercasePipe,
        MakeFirstLatterUppercaseForArrayPipe,
        TodayDate,
        CleanBackslash,
        AddAdditionalZeroToTimePipe,
        ChangeSpaceToDashPipe,
        EnterToInPipe,
        MatSnackBarModule,
        AuthorizationUpdatedComponent,
        ServerDisconnectedErrorComponent,
        NetworkConnectionErrorComponent,
        DarkModeToggleComponent,
        OpenButtonComponent
    ],
    providers: [PanelOutputsService, NotificationService, UserService, AccessPointService, MonitoringService,
        { provide: HTTP_INTERCEPTORS, useClass: DateResponseInterceptor, multi: true }]
})
export class SharedModule {
  constructor(overlayContainer: OverlayContainer) {
    overlayContainer.getContainerElement().classList.add('dark-theme');
  }
}
